// /var/www/ohcampus_frontend_source/src/pages/AnalyticsDashboard.jsx
// ---------------------------------------------------------------------------
//  Role-aware Analytics Dashboard.
//  Counselor → self view, Team Lead → team view, Admin → org-wide view.
//  Data: GET /api/analytics/dashboard
//  Charts: recharts (already in package.json).
// ---------------------------------------------------------------------------
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";
import {
  TrendingUp, TrendingDown, Users, Award, IndianRupee,
  Target, GraduationCap, AlertCircle, Activity,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/layout/Navbar";
import { AdminLayout } from "../components/layout/AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL || "";
const PALETTE = ["#0288D1", "#00AEEF", "#8FDC2B", "#F59E0B", "#EF4444", "#A78BFA", "#10B981", "#64748B"];

function Kpi({ label, value, icon: Icon, accent = "blue", suffix = "" }) {
  const colorMap = {
    blue:  "bg-sky-50 text-sky-700 border-sky-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red:   "bg-red-50 text-red-700 border-red-200",
    teal:  "bg-teal-50 text-teal-700 border-teal-200",
  };
  return (
    <div className={`border rounded-xl p-4 ${colorMap[accent]} flex items-start gap-3`}
         data-testid={`analytics-kpi-${accent}`}>
      <div className="bg-white/60 rounded-lg p-2"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-2xl font-semibold mt-1">{value}{suffix}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

const fmt = (n) => (n || 0).toLocaleString("en-IN");

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const isAdminUser = user && (user.role === "admin" || user.role === "super_admin");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIt = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("ohcampus_token");
        const res = await axios.get(`${API}/api/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchIt();
  }, []);

  const roleLabel = useMemo(() => {
    const role = data?.scope?.role;
    if (role === "admin")     return "Organisation-wide view";
    if (role === "manager")   return "Admission-Manager view";
    if (role === "team_lead") return "Team Lead view";
    return "My performance";
  }, [data]);

  // Layout shim — wraps content with the correct chrome (AdminLayout sidebar
  // for admin/super_admin, top Navbar for counselor + team-lead).
  const LayoutShell = ({ children }) => {
    if (isAdminUser) {
      return <AdminLayout>{children}</AdminLayout>;
    }
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        {children}
      </div>
    );
  };

  if (loading) {
    return (
      <LayoutShell>
      <div className="p-6 max-w-screen-2xl mx-auto" data-testid="analytics-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl" />)}
          </div>
          <div className="h-64 bg-slate-100 rounded-xl" />
        </div>
      </div>
      </LayoutShell>
    );
  }
  if (error) {
    return (
      <LayoutShell>
      <div className="p-6 max-w-screen-2xl mx-auto" data-testid="analytics-error">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div>
            <h3 className="font-semibold text-red-800">Analytics unavailable</h3>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
      </LayoutShell>
    );
  }

  const {
    kpis, lead_funnel, admissions_trend, conversion_trend,
    lead_sources, top_performers, scholarship_funnel, top_colleges, scope,
  } = data;

  const showTopPerformers = ["admin", "manager", "team_lead"].includes(scope.role);
  const showTopColleges   = ["admin", "manager", "team_lead"].includes(scope.role);

  return (
    <LayoutShell>
    <div className="p-6 max-w-screen-2xl mx-auto space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-6 h-6 text-sky-600" /> Analytics Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {roleLabel}
            {scope.counselor_count > 1 && (
              <span className="ml-2 bg-sky-50 text-sky-700 text-xs px-2 py-0.5 rounded-full">
                {scope.counselor_count} counselor{scope.counselor_count === 1 ? "" : "s"} in scope
              </span>
            )}
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi label="Total Leads"   value={fmt(kpis.total_leads)}      icon={Users}  accent="blue"  />
        <Kpi label="Admissions"    value={fmt(kpis.total_admissions)} icon={Award}  accent="green" />
        <Kpi label="Conversion"    value={kpis.overall_conversion}    icon={Target} accent={kpis.overall_conversion >= 15 ? "green" : "blue"} suffix="%" />
      </div>

      {/* Admissions + Conversion trend (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Admissions Trend" subtitle="Closed admissions per month · last 12 months" testid="chart-admissions-trend">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={admissions_trend}>
              <defs>
                <linearGradient id="admFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0288D1" stopOpacity={0.5}/>
                  <stop offset="100%" stopColor="#0288D1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="admissions" stroke="#0288D1" strokeWidth={2} fill="url(#admFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conversion Rate Trend" subtitle="Admissions ÷ Leads · monthly" testid="chart-conversion-trend">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={conversion_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis yAxisId="L" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 11, fill: "#64748B" }} unit="%" />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="L" type="monotone" dataKey="leads"      stroke="#94A3B8" strokeWidth={2} dot={false} name="Leads" />
              <Line yAxisId="L" type="monotone" dataKey="admissions" stroke="#8FDC2B" strokeWidth={2} dot={false} name="Admissions" />
              <Line yAxisId="R" type="monotone" dataKey="conversion_pct" stroke="#0288D1" strokeWidth={3} dot={{ r: 3 }} name="Conv %" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Lead funnel + Source distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Lead Funnel" subtitle="Pipeline at a glance" testid="chart-lead-funnel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={lead_funnel} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis dataKey="stage" type="category" tick={{ fontSize: 12, fill: "#475569" }} width={110} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {lead_funnel.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lead Sources" subtitle="Where leads come from" testid="chart-lead-sources">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={lead_sources} dataKey="count" nameKey="source"
                   cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {lead_sources.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Scholarship Pipeline" subtitle="Applications by stage" testid="chart-scholarship-funnel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={scholarship_funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#64748B" }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8FDC2B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top performers + Top colleges (admin/manager/team-lead only) */}
      {(showTopPerformers || showTopColleges) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {showTopPerformers && (
            <ChartCard title="Top Performing Counselors" subtitle="By admissions count" testid="chart-top-performers">
              {top_performers.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">No admissions data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={top_performers} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis dataKey="counselor_name" type="category" tick={{ fontSize: 11, fill: "#475569" }} width={140} />
                    <Tooltip />
                    <Bar dataKey="admissions" fill="#0288D1" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          )}

          {showTopColleges && (
            <ChartCard title="Top-Converting Colleges" subtitle="By admissions" testid="chart-top-colleges">
              {top_colleges.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">No admissions data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={top_colleges} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis dataKey="college" type="category" tick={{ fontSize: 11, fill: "#475569" }} width={170} />
                    <Tooltip />
                    <Bar dataKey="admissions" fill="#00AEEF" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          )}
        </div>
      )}

      {/* Footer note */}
      <p className="text-xs text-slate-400 text-center pt-2">
        Charts auto-refresh on every page visit. Last fetched at {new Date().toLocaleString()}.
      </p>
    </div>
    </LayoutShell>
  );
}
